import { FullScreenControl, SigmaContainer, ZoomControl } from "@react-sigma/core";
import { createNodeImageProgram } from "@sigma/node-image";
import { DirectedGraph } from "graphology";
import { FC, useEffect, useMemo, useState } from "react";
import { BiBookContent, BiRadioCircleMarked } from "react-icons/bi";
import { BsArrowsFullscreen, BsFullscreenExit, BsZoomIn, BsZoomOut } from "react-icons/bs";
import { GrClose } from "react-icons/gr";
import { Settings } from "sigma/settings";

import { drawHover, drawLabel, drawNode } from "../canvas-utils";
import { Dataset, FiltersState } from "../types";
import TopicClustersPanel from "./TopicClustersPanel";
import CompanyClustersPanel from "./CompanyClustersPanel";
import DescriptionPanel from "./DescriptionPanel";
import GraphDataController from "./GraphDataController";
import GraphEventsController from "./GraphEventsController";
import GraphSettingsController from "./GraphSettingsController";
import GraphTitle from "./GraphTitle";
import SearchField from "./SearchField";

const Root: FC = () => {
  const [showContents, setShowContents] = useState(false);
  const [dataReady, setDataReady] = useState(false);
  const [dataset, setDataset] = useState<Dataset | null>(null);
  const [filtersState, setFiltersState] = useState<FiltersState>({
    clusters: {},
  });
  const [hoveredNode, setHoveredNode] = useState<string | null>(null);

  const sigmaSettings: Partial<Settings> = useMemo(
    () => ({
      nodeProgramClasses: {
        image: createNodeImageProgram({
          size: { mode: "force", value: 256 },
        }),
      },
      defaultDrawNodeLabel: drawLabel,
      defaultDrawNodeHover: drawHover,
      renderNode: drawNode,
      defaultNodeType: "circle",
      defaultEdgeType: "arrow",
      labelDensity: 0.07,
      labelGridCellSize: 60,
      labelRenderedSizeThreshold: 15,
      labelFont: "Lato, sans-serif",
      zIndex: true,
    }),
    []
  );

  useEffect(() => {
    fetch(`/leetcode_graph_data.json`)
      .then((res) => res.json())
      .then((data: Dataset) => {
        setDataset(data);
        const clusters = {
          ...data.topicClusters.reduce((acc, cluster) => ({ ...acc, [`topic-${cluster.key}`]: true }), {}),
          ...data.companyClusters.reduce((acc, cluster) => ({ ...acc, [`company-${cluster.key}`]: true }), {}),
        };
        setFiltersState({ clusters });
        requestAnimationFrame(() => setDataReady(true));
      })
      .catch((error) => {
        console.error("Error fetching dataset:", error);
      });
  }, []);  

  const toggleCluster = (clusterKey: string) => {
    setFiltersState((prevState) => ({
      ...prevState,
      clusters: {
        ...prevState.clusters,
        [clusterKey]: !prevState.clusters[clusterKey]
      }
    }));
  };

  const setClusters = (newClusters: Record<string, boolean>, type: 'topic' | 'company') => {
    setFiltersState((prevState) => ({
      ...prevState,
      clusters: {
        ...prevState.clusters,
        ...Object.keys(newClusters).reduce((acc, key) => ({
          ...acc,
          [`${type}-${key}`]: newClusters[key]
        }), {})
      }
    }));
  };

  if (!dataset) return null;

  return (
    <div id="app-root" className={showContents ? "show-contents" : ""}>
      <SigmaContainer graph={DirectedGraph} settings={sigmaSettings} className="react-sigma">
        <GraphSettingsController hoveredNode={hoveredNode} />
        <GraphEventsController setHoveredNode={setHoveredNode} />
        <GraphDataController dataset={dataset} filters={filtersState} />

        {dataReady && (
          <>
            <div className="controls">
              <div className="react-sigma-control ico">
                <button
                  type="button"
                  className="show-contents"
                  onClick={() => setShowContents(true)}
                  title="Show caption and description"
                >
                  <BiBookContent />
                </button>
              </div>
              <FullScreenControl className="ico">
                <BsArrowsFullscreen />
                <BsFullscreenExit />
              </FullScreenControl>
              <ZoomControl className="ico">
                <BsZoomIn />
                <BsZoomOut />
                <BiRadioCircleMarked />
              </ZoomControl>
            </div>
            <div className="contents">
              <div className="ico">
                <button
                  type="button"
                  className="ico hide-contents"
                  onClick={() => setShowContents(false)}
                  title="Hide caption and description"
                >
                  <GrClose />
                </button>
              </div>
              <GraphTitle filters={filtersState} />
              <div className="panels">
                <SearchField filters={filtersState} />
                <DescriptionPanel />
                <TopicClustersPanel
                  clusters={dataset.topicClusters}
                  filters={filtersState}
                  setClusters={(clusters) => setClusters(clusters, 'topic')}
                  toggleCluster={toggleCluster}
                />
                <CompanyClustersPanel
                  clusters={dataset.companyClusters}
                  filters={filtersState}
                  setClusters={(clusters) => setClusters(clusters, 'company')}
                  toggleCluster={toggleCluster}
                />
              </div>
            </div>
          </>
        )}
      </SigmaContainer>
    </div>
  );
};

export default Root;
